import React, { useCallback, useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/browser";
import qlog from "views_shared/app/javascript/scripts/qlog";
import { authenticatedHeadersHash } from "views_shared/app/javascript/scripts/authentication";
import ActivityTimeline from "../shared/ActivityTimeline";
import RequestDetail from "./RequestDetail";
import AdminIntel from "./AdminIntel";
import ApprovalPanel from "./ApprovalPanel";
import AssignmentBanner from "./AssignmentBanner";
import ContentModResults from "./ContentModResults";

const SUPABASE_URL = () => globalThis.Qwoted.SUPABASE_API_EXTERNAL_URL;
const BLOCKING_CHECK_TYPES = ["safety", "substance", "fitness", "completeness"];
const POLL_COMPLETE_CHECK_TYPES = [...BLOCKING_CHECK_TYPES, "rate_limit"];
const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 90000;

function hasAllFreshModResults(results, contentHash) {
  if (!contentHash || !results.length) return false;
  const matching = results.filter((r) => r.content_hash === contentHash);
  const freshChecks = new Set(matching.map((r) => r.check_name));
  return POLL_COMPLETE_CHECK_TYPES.every((ct) => freshChecks.has(ct));
}

function RequestShow({ requestId }) {
  const [request, setRequest] = useState(null);
  const [contentModResults, setContentModResults] = useState([]);
  const [recentRequests, setRecentRequests] = useState([]);
  const [outletConfig, setOutletConfig] = useState(null);
  const [profileConfig, setProfileConfig] = useState(null);
  const [fullRequest, setFullRequest] = useState(null);
  const [fullRequestError, setFullRequestError] = useState(false);
  const [tags, setTags] = useState([]);
  const [outletDetail, setOutletDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reprocessing, setReprocessing] = useState(false);
  const [reprocessingSince, setReprocessingSince] = useState(null);
  const [reprocessingHash, setReprocessingHash] = useState(null);
  const fetchVersion = useRef(0);
  const pollRef = useRef(null);
  const pollTimeoutRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
    pollRef.current = null;
    pollTimeoutRef.current = null;
    setReprocessing(false);
    setReprocessingSince(null);
    // reprocessingHash intentionally kept — ContentModResults uses it to select
    // the correct result set even after polling ends, preventing late stale
    // retries from displacing the current run's results.
  }, []);

  const fetchData = useCallback(
    async ({ silent = false } = {}) => {
      fetchVersion.current += 1;
      const version = fetchVersion.current;
      try {
        if (!silent) {
          setLoading(true);
          setError(null);
          setContentModResults([]);
          setRecentRequests([]);
          setOutletConfig(null);
          setProfileConfig(null);
          setFullRequest(null);
          setFullRequestError(false);
          setTags([]);
          setOutletDetail(null);
        }

        const headers = {
          ...authenticatedHeadersHash(),
          "Content-Type": "application/json",
        };

        // Primary fetch — fatal: this is the only request that can hard-fail the page
        const requestRes = await fetch(`${SUPABASE_URL()}/rest/v1/admin_review_queue?id=eq.${requestId}`, {
          headers: {
            ...headers,
            Accept: "application/vnd.pgrst.object+json",
          },
        });

        if (version !== fetchVersion.current) return;

        if (!requestRes.ok) {
          if (requestRes.status === 406) {
            setError("Request not found");
          } else {
            setError(`Failed to load request (${requestRes.status})`);
          }
          return;
        }

        const requestData = await requestRes.json();
        setRequest(requestData);

        // Auxiliary fetches — best-effort: failures degrade individual panels, not the whole page
        const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
        const results = await Promise.allSettled([
          fetch(
            `${SUPABASE_URL()}/rest/v1/admin_content_mod_results?request_id=eq.${requestId}&order=created_at.desc`,
            {
              headers,
            },
          ),
          requestData.media_outlet_id
            ? fetch(
                `${SUPABASE_URL()}/rest/v1/request_approval_configs?configurable_type=eq.Id::MediaOutlet&configurable_id=eq.${requestData.media_outlet_id}`,
                { headers },
              )
            : Promise.resolve({ ok: true, json: () => Promise.resolve([]) }),
          fetch(
            `${SUPABASE_URL()}/rest/v1/request_approval_configs?configurable_type=eq.Id::Profile::MediaProfile&configurable_id=eq.${requestData.profile_id}`,
            { headers },
          ),
          fetch(
            `${SUPABASE_URL()}/rest/v1/admin_review_queue?profile_id=eq.${requestData.profile_id}&posted_at=not.is.null&posted_at=gte.${thirtyDaysAgo}&select=id,posted_at&order=posted_at.desc`,
            { headers },
          ),
          fetch(`${SUPABASE_URL()}/rest/v1/requests?id=eq.${requestId}`, {
            headers: {
              ...headers,
              Accept: "application/vnd.pgrst.object+json",
            },
          }),
          fetch(
            `${SUPABASE_URL()}/rest/v1/tags?tagged_type=eq.Interact::Request&tagged_id=eq.${requestId}&select=hashtag_name`,
            { headers },
          ),
          requestData.media_outlet_id
            ? fetch(`${SUPABASE_URL()}/rest/v1/admin_outlets?id=eq.${requestData.media_outlet_id}&select=id,domain`, {
                headers: {
                  ...headers,
                  Accept: "application/vnd.pgrst.object+json",
                },
              })
            : Promise.resolve({ ok: true, json: () => Promise.resolve(null) }),
        ]);

        if (version !== fetchVersion.current) return;

        const [modResult, outletResult, profileResult, recentResult, fullResult, tagsResult, outletDetailResult] =
          results;

        // Decode helper: treats JSON parse failures as non-fatal so one bad
        // response body doesn't take down the whole page.
        const decodeOr = async (settled, fallback, label) => {
          if (settled.status === "fulfilled" && settled.value.ok) {
            try {
              return await settled.value.json();
            } catch (err) {
              qlog.warn(`[RequestShow] ${label}: JSON decode failed`, err);
              Sentry.captureMessage(`RequestShow aux fetch: ${label} decode failed`, "warning");
              return fallback;
            }
          }
          if (settled.status === "rejected") {
            qlog.warn(`[RequestShow] ${label}: network error`, settled.reason);
            Sentry.captureMessage(`RequestShow aux fetch: ${label} network error`, "warning");
          } else if (settled.status === "fulfilled" && !settled.value.ok) {
            qlog.warn(`[RequestShow] ${label}: HTTP ${settled.value.status}`);
            Sentry.captureMessage(`RequestShow aux fetch: ${label} HTTP ${settled.value.status}`, "warning");
          }
          return fallback;
        };

        setContentModResults(await decodeOr(modResult, [], "content_mod_results"));

        const outletConfigs = await decodeOr(outletResult, [], "outlet_config");
        setOutletConfig(outletConfigs[0] || null);

        const profileConfigs = await decodeOr(profileResult, [], "profile_config");
        setProfileConfig(profileConfigs[0] || null);

        setRecentRequests(await decodeOr(recentResult, [], "recent_requests"));

        const fullData = await decodeOr(fullResult, null, "full_request");
        if (fullData) {
          setFullRequest(fullData);
        } else {
          setFullRequestError(true);
        }

        setTags(await decodeOr(tagsResult, [], "tags"));
        setOutletDetail(await decodeOr(outletDetailResult, null, "outlet_detail"));
      } catch (err) {
        if (version === fetchVersion.current && !silent) {
          setError(err.message || "Failed to load request");
        }
      } finally {
        if (version === fetchVersion.current) {
          setLoading(false);
        }
      }
    },
    [requestId],
  );

  const handleResubmit = useCallback(async () => {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
    const res = await fetch(`/admin/api/requests/${requestId}/resubmit`, {
      method: "POST",
      headers: {
        ...authenticatedHeadersHash(),
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
      },
    });

    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      return { ok: false, error: data.error || "Re-evaluation failed" };
    }

    const data = await res.json();
    setReprocessing(true);
    setReprocessingSince(data.resubmitted_at);
    setReprocessingHash(data.content_hash);

    // Start polling for fresh data
    pollRef.current = setInterval(() => fetchData({ silent: true }), POLL_INTERVAL_MS);

    // Safety timeout: stop polling after 90s regardless
    pollTimeoutRef.current = setTimeout(() => {
      stopPolling();
      fetchData({ silent: true });
    }, POLL_TIMEOUT_MS);

    return { ok: true };
  }, [requestId, fetchData, stopPolling]);

  const handleFieldSave = useCallback(
    async (field, value) => {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
      const res = await fetch(`/admin/api/requests/${requestId}/edit`, {
        method: "PATCH",
        headers: {
          ...authenticatedHeadersHash(),
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({ request: { [field]: value } }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "Save failed");
      }

      // Silent refresh: update page data without the full-page loading spinner,
      // so the caller's own save UI (modal spinner, inline edit) isn't disrupted.
      fetchData({ silent: true });
    },
    [requestId, fetchData],
  );

  const handleTypeChange = useCallback(
    async (fields) => {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
      const res = await fetch(`/admin/api/requests/${requestId}/edit`, {
        method: "PATCH",
        headers: {
          ...authenticatedHeadersHash(),
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({ request: fields }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "Save failed");
      }

      fetchData({ silent: true });
    },
    [requestId, fetchData],
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Detect when reprocessing completes: state changed from unprocessed AND
  // all blocking content mod results exist for the current run's content hash.
  useEffect(() => {
    if (!reprocessing || !request) return;

    const isUnprocessed = !request.needs_manual_approval_at && !request.approved_at && !request.ghosted_at;

    if (!isUnprocessed && hasAllFreshModResults(contentModResults, reprocessingHash)) {
      stopPolling();
    }
  }, [reprocessing, request, contentModResults, reprocessingHash, stopPolling]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  if (loading) {
    return (
      <div className="text-center py-5">
        <i className="fa fa-spinner fa-spin fa-2x text-muted" />
        <p className="text-muted mt-3">Loading request...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-5">
        <i className="fa fa-exclamation-triangle fa-2x text-danger" />
        <p className="text-danger mt-3">{error}</p>
        <a href="/admin/requests" className="btn btn-outline-primary">
          Back to Requests
        </a>
      </div>
    );
  }

  if (!request) return null;

  const timelineEntities = [
    { type: "Interact::Request", id: String(request.id) },
    request.media_outlet_id ? { type: "Id::MediaOutlet", id: String(request.media_outlet_id) } : null,
    request.profile_id ? { type: "Id::Profile::MediaProfile", id: String(request.profile_id) } : null,
  ].filter(Boolean);

  return (
    <div>
      {/* Assignment banner — top of page for visibility */}
      <AssignmentBanner request={request} onActionComplete={fetchData} />

      {/* Row 1: Request detail + Admin intel */}
      <div className="row mb-4">
        <div className="col-lg-8">
          <RequestDetail
            request={request}
            fullRequest={fullRequest}
            fullRequestError={fullRequestError}
            tags={tags}
            onFieldSave={handleFieldSave}
          />
        </div>
        <div className="col-lg-4">
          <AdminIntel
            request={request}
            fullRequest={fullRequest}
            outletConfig={outletConfig}
            profileConfig={profileConfig}
            outletDetail={outletDetail}
            onFieldSave={handleFieldSave}
            onTypeChange={handleTypeChange}
          />
        </div>
      </div>

      {/* Row 2: Approval panel + Content mod results */}
      <div className="row mb-4">
        <div className="col-lg-8">
          <ApprovalPanel
            request={request}
            outletConfig={outletConfig}
            profileConfig={profileConfig}
            onActionComplete={fetchData}
            onOutletConfigChange={setOutletConfig}
            onProfileConfigChange={setProfileConfig}
            reprocessing={reprocessing}
            onResubmit={handleResubmit}
          />
        </div>
        <div className="col-lg-4">
          <ContentModResults
            results={contentModResults}
            request={request}
            recentRequests={recentRequests}
            reprocessingSince={reprocessingSince}
            reprocessingHash={reprocessingHash}
          />
        </div>
      </div>

      {/* Row 3: Activity timeline */}
      <div className="row">
        <div className="col-lg-8">
          <ActivityTimeline entities={timelineEntities} />
        </div>
      </div>
    </div>
  );
}

export default RequestShow;
